import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { NegotiatorTableData } from 'app/models/admin.model';
import { CaseResponseModel } from 'app/models/case-response.model';
import { AdminService } from 'app/services/admin.service';
import { AlertsService } from 'app/ui-components/alerts/alerts.service';
import { UserProfileRecord } from 'app/users/user-profile-record';
import { UserService } from 'app/users/user.service';

@Component({
  selector: 'app-negotiator-dashboard',
  templateUrl: './negotiator-dashboard.component.html',
  styleUrls: ['./negotiator-dashboard.component.scss']
})
export class NegotiatorDashboardComponent implements OnInit {
  private loadingComplete: boolean = true;
  private showResults: boolean = false;
  isSmallView: boolean = false;
  appCardData!: NegotiatorTableData[];
  appCardDataPag!: NegotiatorTableData[];
  private dataSource = new MatTableDataSource<NegotiatorTableData>();
  public displayColumns: string[] = [
    'fileNumber', 'form', 'submittedDate', 
    'firstSuggestedName', 'contactName', 
    'company', 'contactEmail', 'status','viewApplication'
  ];

  pageCardSize: number = 10; 
  currentPage: number = 0;
  private paginator!: MatPaginator;
  appListLength!: number;
  
  @ViewChild(MatSort) negotiatorSort!: MatSort;
  applicationData!: NegotiatorTableData[];
  
  formTypeMap = { 
    'A': 'Form A - Single Entity Drug and Salt',
    'B': 'Form B - Single Entity' ,
    'C': 'Form C - Modified Salt Form' ,
    'D': 'Form D - USAN Revised Application' ,
    'E': 'Form E - Contact Lens Material' ,
    'F': 'Form F - Biologics' 
  };

  appTypes: string[] = ['Active', 'Approved / Closed'];

  constructor(
    private responsive: BreakpointObserver,
    private sortAnnouncer: LiveAnnouncer,
    protected userService: UserService, 
    protected adminService: AdminService, 
    protected alertService: AlertsService,
    protected router: Router
  ) {}

  ngOnInit(): void {
    this.retrieveAssignedApplications('Active');
    this.determineViewMode();
  }

  @ViewChild(MatPaginator) set matPaginator(paginator: MatPaginator) {
    this.paginator = paginator;
    this.dataSource.paginator = this.paginator;
  }

  retrieveAssignedApplications(status: string) {
    this.loadingComplete = false;
    let email = this.profile.email;
    let uniqueId = this.profile.uniqueId;
    if(email && uniqueId) {
      let request = {
        'email' : email,
        'status': [ status]
      }
      this.adminService.getNegotiatorDashBoard(request).subscribe({
        next: response => {
          if(response.Errors.length > 0) {
            this.alertService.displayError("Error occurred while retrieving data" + response.ErrorMessage);
          } else {
            if(response.Data) {
              let cases: NegotiatorTableData[] = [];
              if(response.Data.length > 0) {
                response.Data.forEach(element => {
                  let model: CaseResponseModel = CaseResponseModel.parseValues(element);
                  let data: NegotiatorTableData = {
                    caseSysId: element.caseSysId,
                    sys_id: element.sys_id,
                    fileNumber: element.u_file_number_single_entity, 
                    form: this.parseFormType(model.u_form_type),
                    submittedDate: model.u_application_submitted_date,
                    firstSuggestedName: this.firstSuggestedName(model),
                    contactName: element.u_primary_contact_first_name + ' ' + element.u_primary_contact_last_name,
                    company: element.u_applicant,
                    contactEmail: element.u_email_address,
                    status: element.u_case_status
                  };
                  cases.push(data);
                });
              } 
              this.dataSource.data = cases;
              this.appListLength = cases.length;
              this.applicationData = cases;
              if(this.isSmallView) {
                this.appCardData = cases;
                this.updateItemsForPagination();
              }
            }
          }
          this.loadingComplete = true;
          this.showResults = true;
        },
        error: error => {
          this.alertService.displayError(`Error occurred while retrieving assigned applications. ${error.message}`);
          this.loadingComplete = true;
        }
      })
    }
  }

  onPageChange(event: PageEvent) {
    this.pageCardSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.updateItemsForPagination();
  }

  updateItemsForPagination() {
    if(!this.appCardData) {
      return;
    }
    const start = this.currentPage * this.pageCardSize;
    const end = start + this.pageCardSize;
    this.appCardDataPag = this.appCardData.slice(start, end);
  }

  public firstSuggestedName(model: CaseResponseModel): string {
    if(this.parseFormType(model.u_form_type) =='D') {
      return model.u_name_for_which_you_are_requesting_a_revision;
    }

    let names = model.u_suggested_names_in_order_of_preference_for_single_entity;

    if(!names) {
      return '';
    }

    let nameArr = names?.split(",");
    let name = '';
    if(nameArr && nameArr.length > 0) {
      name = nameArr[0];
    }
    return name;
  }

  parseFormType(str: string) : string {
    if(str.includes('Form '))
      return str.split('Form ')[1];
    else if(str.includes('Form'))
      return str.split('Form')[1];
    return str;
  }

  shorten(name: string) : string {
    if(name)
      return name.charAt(0) + '.' + name.substring(name.indexOf(' ') + 1);
    return name;
  }

  public viewApplication(data: NegotiatorTableData) {
    this.router.navigate(['/usan/application'],
      { queryParams : { formType: data.form, id: data.caseSysId, source: 'negotiator-dashboard'}});
  } 

  determineViewMode() {
    this.responsive.observe([Breakpoints.HandsetLandscape, Breakpoints.HandsetPortrait,
      Breakpoints.TabletPortrait, Breakpoints.TabletLandscape 
    ])
        .subscribe(result => {
          const breakpoints = result.breakpoints;
          this.isSmallView = false;
          if (breakpoints[Breakpoints.HandsetPortrait] || breakpoints[Breakpoints.HandsetLandscape] ||
            breakpoints[Breakpoints.TabletPortrait] || breakpoints[Breakpoints.TabletLandscape]) {
            this.isSmallView = true;
          }
        });
  }

  get profile(): UserProfileRecord {
    return this.userService.profile;
  }

  get isShowingResults(): boolean {
    return this.showResults;
  }

  get isLoadingComplete(): boolean {
    return this.loadingComplete;
  }

  sortChangeUpdate(sortChange: Sort) {
    const data = this.applicationData.slice();
    let column = sortChange.active;
    let dir = sortChange.direction;
    if (!column || dir === '') {
      //no change
      return;
    }
    const isAsc = dir ==='asc';
    this.dataSource.data = data.sort((a,b) => {
      let aValue = typeof a[column] === 'string' ? a[column].toLowerCase() : a[column];
      let bValue = typeof b[column] === 'string' ? b[column].toLowerCase() : b[column];
      return this.compareValues(aValue, bValue, isAsc);

    });
    if(sortChange.direction) { //to announce sorting change for assistive technology
      this.sortAnnouncer.announce('Sorted '+ sortChange.direction);
    } else {
      this.sortAnnouncer.announce('Sort cleared');
    }
  }
  
  compareValues( a: string|number, b: string|number, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  selectedTabChangeApp(appType: MatTabChangeEvent) {
      this.dataSource.data = [];
      this.loadingComplete = false;
      this.showResults = false;
      let appTypeSelected = this.appTypes[appType.index];
      if(appTypeSelected.includes('Approved'))
        appTypeSelected = 'Approved'; //Approved will also include Closed cases as well
      this.retrieveAssignedApplications(appTypeSelected)
    }
}
